/**
 * @file callRecording.js
 * @description
 * On-device call recording: preferences, the native recorder bridge and the
 * resilient upload queue.
 *
 * Native side (`NativeModules.HappyCallRecorder`, iOS custom audio device +
 * Android audio tap) mixes the microphone (left) and the remote party (right)
 * into an AAC .m4a. The native tap is only installed when the user enabled
 * recording (the opt-in is read at process start), so turning the feature on
 * for the first time needs one app restart — `applyRecordingOptIn` reports it.
 *
 * Recordings are written to the app's document directory and uploaded to
 * `POST /api/voip/call-recordings` by a persisted queue that survives app
 * kills, retries with backoff and deletes the local file once the server has
 * the audio. Everything here is guarded: a build without the native module is
 * a safe no-op and recording never interferes with the call itself.
 *
 * Pure JavaScript (ESM).
 */

import {NativeModules, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system';
import Observables from '@happy-gastro/global-observables';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';
import {
    DEFAULT_RECORDING_PREFS,
    UPLOAD_PERMANENT_FAILURES,
    buildUploadFields,
    isUploadDue,
    isUploadExpired,
    normalizeRecordingPrefs,
    uploadRetryDelayMs,
} from '../helpers/voipRecordingPolicy';

const PREFS_KEY = 'voipRecordingPrefs';
const QUEUE_KEY = 'voipRecordingUploadQueue';
const UPLOAD_URL = `${ApiConfig.apiURL}/api/voip/call-recordings`;
const RECORDINGS_DIR = 'call-recordings/';

/** Notified with the new preferences whenever they change. */
export const RECORDING_PREFS_EVENT = 'voip:recordingPrefs';

let prefsCache = null;
let current = null; // {id, path, meta}
let flushing = null;

const native = () => (Platform.OS === 'ios' || Platform.OS === 'android' ? NativeModules.HappyCallRecorder || null : null);

/** Random RFC-4122-ish id (idempotency key of an upload). */
function newId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;

        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/* -------------------------------------------------------------------------- */
/*  Preferences                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Stored recording preferences (cached after the first read).
 * @returns {Promise<{enabled: boolean, auto: string}>}
 */
export async function loadRecordingPrefs() {
    if (prefsCache) {
        return prefsCache;
    }
    try {
        const raw = await AsyncStorage.getItem(PREFS_KEY);
        prefsCache = normalizeRecordingPrefs(raw ? JSON.parse(raw) : null);
    } catch (_error) {
        prefsCache = {...DEFAULT_RECORDING_PREFS};
    }

    return prefsCache;
}

/**
 * Persist (part of) the preferences, sync the native opt-in and notify listeners.
 * @param {{enabled?: boolean, auto?: string}} patch Changes.
 * @returns {Promise<{prefs: object, restartRequired: boolean}>}
 */
export async function saveRecordingPrefs(patch) {
    const prefs = normalizeRecordingPrefs({...(await loadRecordingPrefs()), ...(patch || {})});
    prefsCache = prefs;
    await AsyncStorage.setItem(PREFS_KEY, JSON.stringify(prefs)).catch(() => {});
    const {restartRequired} = await applyRecordingOptIn(prefs.enabled);
    Observables.call(RECORDING_PREFS_EVENT, prefs);

    return {prefs, restartRequired};
}

/**
 * Tell the native layer whether the recording audio tap should be installed on
 * the NEXT process start.
 * @param {boolean} enabled Opt-in.
 * @returns {Promise<{enabled: boolean, active: boolean, restartRequired: boolean}>}
 */
export async function applyRecordingOptIn(enabled) {
    const module = native();
    if (!module || typeof module.setCustomAudioEnabled !== 'function') {
        return {enabled: Boolean(enabled), active: false, restartRequired: false};
    }
    try {
        const result = await module.setCustomAudioEnabled(Boolean(enabled));

        return {enabled: Boolean(result?.enabled), active: Boolean(result?.active), restartRequired: Boolean(result?.restartRequired)};
    } catch (_error) {
        return {enabled: Boolean(enabled), active: false, restartRequired: false};
    }
}

/** Whether this binary ships the native recorder at all (older builds do not). */
export function isRecordingSupported() {
    return native() !== null;
}

/** Whether the native recorder is installed in THIS process (false until restart after first opt-in). */
export async function isRecordingAvailable() {
    const module = native();
    if (!module || typeof module.isAvailable !== 'function') {
        return false;
    }
    try {
        return Boolean(await module.isAvailable());
    } catch (_error) {
        return false;
    }
}

/**
 * Re-assert the stored opt-in natively at boot (covers a reinstall / cleared native defaults).
 * @returns {Promise<void>}
 */
export async function syncRecordingOptIn() {
    const prefs = await loadRecordingPrefs();
    if (prefs.enabled) {
        await applyRecordingOptIn(true);
    }
}

/* -------------------------------------------------------------------------- */
/*  Recording a call                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Start recording the current call.
 * @param {{direction: string, number: string, startedAt: string, callId?: string, sessionId?: string}} meta Call metadata.
 * @returns {Promise<{ok: boolean, reason: (string|null)}>} `reason`: unavailable | failed.
 */
export async function startCallRecording(meta = {}) {
    const module = native();
    if (!module || !(await isRecordingAvailable())) {
        return {ok: false, reason: 'unavailable'};
    }
    if (current) {
        await finishCallRecording({}).catch(() => {});
    }
    try {
        const id = newId();
        const base = FileSystem.documentDirectory || '';
        const path = `${base}${RECORDINGS_DIR}${id}.m4a`;
        await FileSystem.makeDirectoryAsync(`${base}${RECORDINGS_DIR}`, {intermediates: true}).catch(() => {});
        await module.start(path);
        current = {id, path, meta: {...meta, clientRecordingId: id}};

        return {ok: true, reason: null};
    } catch (_error) {
        current = null;

        return {ok: false, reason: 'failed'};
    }
}

/** Whether a recording is in progress. */
export function isCallRecording() {
    return current !== null;
}

/**
 * Pause / resume capturing (the file stays one continuous recording).
 * @param {boolean} paused Whether to pause.
 * @returns {Promise<void>}
 */
export async function setCallRecordingPaused(paused) {
    const module = native();
    if (module && current) {
        await module.setPaused(Boolean(paused)).catch(() => {});
    }
}

/**
 * Mirror the call mute into the recording (the local channel records silence while muted).
 * @param {boolean} muted Whether the microphone is muted.
 * @returns {Promise<void>}
 */
export async function setCallRecordingMuted(muted) {
    const module = native();
    if (module && current && typeof module.setLocalMuted === 'function') {
        await module.setLocalMuted(Boolean(muted)).catch(() => {});
    }
}

/**
 * Stop the recording and queue it for upload.
 * @param {{callId?: string, sessionId?: string, durationSeconds?: number}} [final] Call ids / duration known only at the end.
 * @returns {Promise<boolean>} Whether a recording file was queued.
 */
export async function finishCallRecording(final = {}) {
    const module = native();
    const recording = current;
    current = null;
    if (!module || !recording) {
        return false;
    }
    let result = null;
    try {
        result = await module.stop();
    } catch (_error) {
        result = null;
    }
    if (!result || !result.path) {
        return false;
    }
    await enqueueUpload({
        id: recording.id,
        filePath: result.path,
        createdAt: Date.now(),
        attempts: 0,
        fields: buildUploadFields({
            ...recording.meta,
            callId: final.callId || recording.meta.callId,
            sessionId: final.sessionId || recording.meta.sessionId,
            durationSeconds: final.durationSeconds,
            recordedSeconds: Number(result.durationMs) / 1000,
        }),
    });
    flushRecordingUploads().catch(() => {});

    return true;
}

/* -------------------------------------------------------------------------- */
/*  Upload queue                                                              */
/* -------------------------------------------------------------------------- */

async function readQueue() {
    try {
        const raw = await AsyncStorage.getItem(QUEUE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];

        return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
        return [];
    }
}

const writeQueue = (queue) => AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(queue)).catch(() => {});

async function enqueueUpload(entry) {
    const queue = await readQueue();
    queue.push(entry);
    await writeQueue(queue);
}

const deleteFile = (path) => FileSystem.deleteAsync(path, {idempotent: true}).catch(() => {});

/**
 * Upload one queued recording.
 * @param {object} entry Queue entry.
 * @param {string} token Bearer token.
 * @returns {Promise<{ok: boolean, status: number}>}
 */
async function uploadEntry(entry, token) {
    const info = await FileSystem.getInfoAsync(entry.filePath).catch(() => null);
    if (!info || !info.exists) {
        return {ok: false, status: 404}; // nothing left to upload
    }
    const form = new FormData();
    form.append('file', {uri: entry.filePath, name: `${entry.id}.m4a`, type: 'audio/mp4'});
    Object.entries(entry.fields || {}).forEach(([key, value]) => form.append(key, value));
    try {
        const response = await fetch(UPLOAD_URL, {
            method: 'POST',
            headers: {Authorization: `Bearer ${token}`},
            body: form,
        });

        return {ok: response.ok, status: response.status};
    } catch (_error) {
        return {ok: false, status: 0}; // offline / network error → retry later
    }
}

/**
 * Upload every due recording (single-flight). Safe to call often: on app start,
 * after login, on foreground and after each call.
 * @returns {Promise<{uploaded: number, pending: number}>}
 */
export function flushRecordingUploads() {
    if (flushing) {
        return flushing;
    }
    flushing = (async () => {
        let uploaded = 0;
        const queue = await readQueue();
        if (queue.length === 0) {
            return {uploaded, pending: 0};
        }
        const token = await getAccessToken().catch(() => null);
        const remaining = [];
        let authBlocked = !token;
        for (const entry of queue) {
            if (isUploadExpired(entry)) {
                await deleteFile(entry.filePath);
                continue;
            }
            if (authBlocked || !isUploadDue(entry)) {
                remaining.push(entry);
                continue;
            }
            // eslint-disable-next-line no-await-in-loop
            const result = await uploadEntry(entry, token);
            if (result.ok) {
                uploaded += 1;
                // eslint-disable-next-line no-await-in-loop
                await deleteFile(entry.filePath);
            } else if (result.status === 401) {
                authBlocked = true; // signed out / expired — keep everything for the next login
                remaining.push(entry);
            } else if (UPLOAD_PERMANENT_FAILURES.includes(result.status)) {
                // eslint-disable-next-line no-await-in-loop
                await deleteFile(entry.filePath);
            } else {
                const attempts = (entry.attempts || 0) + 1;
                const delay = uploadRetryDelayMs(attempts);
                if (delay === null) {
                    // eslint-disable-next-line no-await-in-loop
                    await deleteFile(entry.filePath);
                } else {
                    remaining.push({...entry, attempts, nextAttemptAt: Date.now() + delay});
                }
            }
        }
        await writeQueue(remaining);

        return {uploaded, pending: remaining.length};
    })().finally(() => {
        flushing = null;
    });

    return flushing;
}
